import { useEffect, useRef, useState } from 'react';
import type { ILeagueItem } from '../types/myLeagues';
import { Images } from '../../../utils/imageConstants';
import { resolvePublicImageUrl } from '../../../helper/helper';
import { FaCalendarDays } from "react-icons/fa6";
import { CiLocationOn } from "react-icons/ci";
import { IoFootballOutline } from "react-icons/io5";
import { TbTournament } from "react-icons/tb";
import { FiEdit2, FiMoreVertical } from "react-icons/fi";

const CREATOR_ROLES = ['ORGANIZER', 'CREATOR', 'ADMIN', 'OWNER'];

interface LeagueCardProps {
  league: ILeagueItem;
  onClick: () => void;
  onEditLeague?: (league: ILeagueItem) => void;
  /** When true, shows "Join League" button instead of making the whole card clickable. For Near You section only. */
  showJoinButton?: boolean;
  /** When false, hides the Created / Participated badge (e.g. guest browsing). */
  showRoleBadge?: boolean;
}

function isCreatorLeague(league: ILeagueItem): boolean {
  const role = (league.roleName ?? '').toUpperCase();
  const hasCreatorPermissions = Boolean(
    league.canEditLeague ||
      league.canAddTournamentToLeague ||
      league.canAddTeamToLeague ||
      league.canAddPlayerToTeam ||
      league.canCreateDivisions
  );

  return hasCreatorPermissions || CREATOR_ROLES.some((r) => role.includes(r));
}

/** League admins / organizers who may use the card "Edit league" action (no delete). */
function canUseLeagueEditMenu(league: ILeagueItem): boolean {
  if (league.canEditLeague === true) return true;
  const role = (league.roleName ?? '').toUpperCase();
  return CREATOR_ROLES.some((r) => role.includes(r));
}

function LeagueCard({
  league,
  onClick,
  onEditLeague,
  showJoinButton = false,
  showRoleBadge = true,
}: LeagueCardProps) {
  const isCreated = isCreatorLeague(league);
  const badgeLabel = isCreated ? '• Created' : '• Participated';
  const showEditMenu = Boolean(
    showRoleBadge && onEditLeague && canUseLeagueEditMenu(league)
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuWrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const venue = league.venue ?? ([league.city, league.state].filter(Boolean).join(', ') || '—');
  const teamsLabel = league.teamsCount != null
    ? `${league.teamsCount} Teams`
    : (league.tournamentIds?.length ? `${league.tournamentIds.length} Tournaments` : '—');
  const oversLabel = league.oversFormat ?? null;

  const imageSrc =
    resolvePublicImageUrl(league.bannerUri) ||
    resolvePublicImageUrl(league.imageUri) ||
    Images.created_image;

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const content = (
    <>
      <div className="myleagues-league-card-content">
        {!showJoinButton && showRoleBadge && (
          <span className="myleagues-league-card-badge">{badgeLabel}</span>
        )}
        <h3 className="myleagues-league-card-name">{league.leagueName}</h3>
        <div className="myleagues-league-card-details">
          <div className="myleagues-league-card-detail">
            <FaCalendarDays size={20} />
            {league.startDate ?? 'dec 20 2025'}
          </div>
          <div className="myleagues-league-card-detail">
            <CiLocationOn />
            {venue}
          </div>

          <div style={{ width: '100%', height: '1px', background: '#4C4C4D40', margin: '10px 0' }}></div>
          <div style={{ display: 'flex', gap: '22px' }}>

            <div className="myleagues-league-card-detail">
              <TbTournament />
              {teamsLabel}
            </div>
            {oversLabel && (
              <div className="myleagues-league-card-detail">
                <IoFootballOutline size={18} />
                {oversLabel}
              </div>
            )}
          </div>
        </div>
        {showJoinButton && (
          <button
            type="button"
            className="myleagues-league-card-join-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            Join League
          </button>
        )}
      </div>
      <div className="myleagues-league-card-image-wrap">
        {showEditMenu && (
          <div
            className="myleagues-league-card-menu"
            ref={menuWrapRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="myleagues-league-card-menu-trigger"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="League actions"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
            >
              <FiMoreVertical size={22} aria-hidden />
            </button>
            {menuOpen && (
              <div className="myleagues-league-card-dropdown" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="myleagues-league-card-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onEditLeague?.(league);
                  }}
                >
                  <FiEdit2 size={16} aria-hidden />
                  <span>Edit League</span>
                </button>
              </div>
            )}
          </div>
        )}
        <img
          src={imageSrc}
          alt=""
          className="myleagues-league-card-image"
          onError={(e) => {
            e.currentTarget.src = Images.created_image;
          }}
        />
      </div>
    </>
  );

  if (showJoinButton) {
    return (
      <div className="myleagues-league-card myleagues-league-card--with-join">
        {content}
      </div>
    );
  }

  return (
    <div
      className="myleagues-league-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleCardKeyDown}
    >
      {content}
    </div>
  );
}

export default LeagueCard;
